import React, { FC, useEffect, useState, useCallback } from 'react'
import cn from 'classnames'
import { WbIcon } from '@workbench/react/dist'
import { TimeSelectOptions } from './TimeSelectOptions'
import classes from './TimeSelect.module.scss'
import { Option } from '../../FilterSelect/types'

interface TimeSelectProps {
  id?: string
  name?: string
  options: Option[]
  label: string
  hint?: string
  value?: string
  disabled?: boolean
  defaultValue?: string
  onChange?: (time: string) => void
}

export const TimeSelect: FC<TimeSelectProps> = ({
  id,
  name,
  options,
  label,
  disabled = false,
  value,
  defaultValue = '',
  onChange,
}) => {
  const [currentValue, setCurrentValueState] = useState(defaultValue)
  const [open, setOpen] = useState(false)
  const [selectedOption, setSelectedOption] = useState<Option>()

  const setCurrentValue = useCallback(
    (value) => {
      setCurrentValueState(value)
      if (onChange) onChange(value)
    },
    [setCurrentValueState, onChange]
  )

  useEffect(() => {
    if (value) setCurrentValueState(value)
  }, [value])

  useEffect(() => {
    if (selectedOption || !currentValue) {
      return
    }
    const option = options.find((o) => o.value === currentValue)
    if (option) {
      setSelectedOption(option)
    } else {
      setCurrentValue('')
    }
  }, [currentValue, options, selectedOption, setCurrentValue])

  const openOptions = useCallback(() => {
    if (!disabled) {
      setOpen(true)
    }
  }, [disabled, setOpen])

  const closeOptions = useCallback(() => setOpen(false), [setOpen])

  const setSelected = useCallback(
    (option) => {
      setSelectedOption(option)
      setCurrentValue(option.value)
      setOpen(false)
    },
    [setSelectedOption, setCurrentValue, setOpen]
  )
  return (
    <div
      className={cn(classes.container, classes.timeSelect, {
        [classes.open]: open,
        [classes.disabled]: disabled,
      })}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={openOptions}
        className={classes.button}
      >
        <label
          htmlFor={id}
          className={cn(classes.label, {
            [classes.selected]: !!selectedOption,
          })}
        >
          {label}
          <input
            id={id}
            type="hidden"
            name={name}
            value={defaultValue}
            data-testid={id}
          />
        </label>

        <div className={classes.selectedOption}>{selectedOption?.label}</div>

        <WbIcon className={classes.caret} name="caret-down" />
      </button>

      <TimeSelectOptions
        options={options}
        onSelect={setSelected}
        onRequestClose={closeOptions}
        open={open}
        data-testid={id}
      />
    </div>
  )
}
